import React, {useEffect} from 'react'
import _ from 'lodash/fp'
import styled from 'styled-components/macro';
import {getColor, isSmallDevice, media, rafThrottle} from "@startlibs/utils";
import {useEnsureRef, useToggle} from "@startlibs/core";
import {css} from "styled-components";
import {desaturate, lighten} from "polished";
import {Icon, Link, SwitchDiv} from "@startlibs/components";

styled;

export const SidebarContainer = styled.div`
  @media print {
    display: none;
  }
  transition: background 0.5s ease;
  ${media.desktop`
    position: sticky;
    align-self: flex-start;
    top: 0rem;
    width: 23rem;
    padding: 9.5rem 2rem 3rem 2rem;
    flex-shrink: 0;
  `}
  ${media.mobile`
    position: fixed;
    top: 5rem;
    left: 0;
    z-index: 120;
    width: 100%;
    ${props => !props.collapsed && `
      background: rgba(0,0,0,0.25);
      height: 100%;
    `}
  `}
`

export const Sidebar = styled(React.forwardRef((props, ref) => {

  const ensuredRef = useEnsureRef(ref)

  React.useEffect(() => {
    let lastPosition = window.pageYOffset;
    let currentY = 0
    const scrollEvent = rafThrottle(() => {
      const delta = window.pageYOffset - lastPosition
      if (!ensuredRef.current) return;
      const {top, height} = ensuredRef.current.getBoundingClientRect()
      const originalTop = top - currentY
      const max = Math.max(0, height + originalTop - window.innerHeight)
      currentY = Math.max(-max, Math.min(currentY - delta, 0))
      ensuredRef.current.style.transform = `translateY(${currentY}px)`
      lastPosition = window.pageYOffset
    })
    window.addEventListener('scroll', scrollEvent)
    return () => window.removeEventListener('scroll', scrollEvent)
  }, [])

  return <div ref={ensuredRef} {...props}/>
}))`
  ${media.desktop`
    padding-bottom: 2rem;
  `}
`

const ArrowDownIcon = styled(Icon)`
  font-size: 20px;
  margin-right: 1.75rem;
  color: ${getColor('gray90')};
`

export const CollapsedSidebarIcon = styled(Icon)`
  float: right;
  font-size: 20px;
  margin-right: 1.25rem;
  color: ${getColor('gray90')};
  height: 4rem;
  width: 4rem;
  line-height: 4rem;
  text-align: center;
  background: ${getColor('gray240')};
  border-radius: 50%;
  z-index: 100;
  position: relative;
`

export const useMobileSidebar = (closeWhen) => {
  const expanded = useToggle(false)
  const isCollapsed = !expanded.isOpen && isSmallDevice()
  useEffect(() => {
    expanded.close()
  },[closeWhen])
  return [isCollapsed,expanded]
}
export const SidebarCard = styled(({collapsed,children,...props}) =>
  <div {...props}><SwitchDiv on={!!collapsed}>{collapsed
    ? <div css="display:flex;align-items:center;justify-content:space-between;">
      <div className="sidebar-heading">
        {collapsed}
      </div>
      <ArrowDownIcon icon="arrow-down-line"/>
    </div>
    : children
  }</SwitchDiv></div>
)`
  overflow: hidden;
  ${media.desktop`
    border-radius: 8px;
    margin-bottom: 1.25rem;
  `}
  .sidebar-heading {
    margin-bottom: 1rem;
    position: relative;
  }
  ${media.mobile`
    margin-bottom: 0;
    ${props => props.collapsed && `
      border-radius: 0 0 8px 8px;  
      .sidebar-heading {
        margin-bottom: 0rem;
      }
    `}
  `}
  transition: 0.5s ease;
  padding: 1rem 0;
  background: white;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.1), 0px 0px 2px 0px rgba(0, 0, 0, 0.25);
  position: relative;
  .sidebar-icons {
    position: absolute;
    top: 0.25rem;
    right: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: ${getColor('gray180')};
  }
  .share-icon {
      cursor: pointer;
      :hover {
        color: ${getColor('gray120')};
      }
    }
  }
  .priority-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 6px;
    font-size: 20px;
    margin-left: 0.5rem;
    ${props => !props.isExpert && css`
      cursor: pointer;
    `}
    :hover {
      ${props => !props.isPriority && css`
        color: ${getColor('gray120')};
      `}
    }
    ${Icon} {
      margin-top: -2px;
    }
    ${props => props.isPriority && css`
      color: ${getColor('warning')};
      background-color: ${getColor('lightYellow')};
      ${props => !props.isExpert && css`
        :hover {
          opacity: 75%;
        }
    `}
  `}
`
export const StepLink = styled(Link)`
  display: block;
  text-decoration: none;
  color: rgba(0,0,0,0.8);
  padding: 9px 2rem 9px 3.5rem;
  & ${StepLink} {
    margin-top: .25rem;
  }
  position: relative;
  font-size: 13px;
  user-select: none;
  ${Icon} {
    font-size: 20px;
    width: 1.25rem;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 1.5rem;
    transform: translateY(-50%);
  }
  ${props => props.selected ? css`
    background: ${props => desaturate(0.6, lighten(0.52, getColor("main")(props)))};;
    font-weight: 600;
    color: ${getColor('main')};
    position: relative;
    cursor: default;
    :before {
      content: '';
      width: 4px;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background: ${getColor('main')};
    }
    ${Icon} {
      color: ${getColor('main')};
    }
    .secondaryActionIcon {
      :hover {
        background: ${props => desaturate(0.5, lighten(0.45, getColor("main")(props)))} !important;
      }
      :active {
        background: ${props => desaturate(0.45, lighten(0.4, getColor("main")(props)))} !important;
      }
    }
  `:`
    :hover {
      cursor: pointer;
      background: rgba(0,0,0,0.05);
      .secondaryActionIcon {
        background: rgba(0,0,0,0.05);
      }
    }
  `}
  .secondaryActionIcon {
    cursor: pointer;
    right: 1rem;
    left: inherit;
    width: 2.5rem;
    height: 2.5rem;
    line-height: 2.5rem;
    border-radius: 50%;
    pointer-events: auto;
    font-size: 23px;
    :hover {
      background: rgba(0,0,0,0.1);
    }
    :active {
      background: rgba(0,0,0,0.15);
    }
  }
  .notificationNumber {
    position: absolute;
    top: 8px;
    cursor: pointer;
    right: 1.5rem;
    left: inherit;
    min-width: 1.75rem;
    padding: 0 0.5rem;
    height: 1.75rem;
    line-height: 1.75rem;
    border-radius: 100px;
    pointer-events: auto;
    color: white;
    font-size: 12px;
    text-align: center;
    background: #c4212f;
    font-weight: 600;
  }
`
export const DashboardLink = styled(Link)`
  color: rgba(0,0,0,0.3);
  margin: 0 2rem 1.25rem;
  display: block;
`
export const SidebarDesc = styled.div`
  color: rgba(0,0,0,0.3);
  font-weight: 600;
  font-size: 11px;
  padding: 0.5rem 1.5rem 0.5rem;
`
export const SidebarInformation = styled.div`
  padding: 0rem 1.5rem 0.5rem;
`
export const SidebarTitle = styled.div`
  color: ${getColor('main')};
  padding: 0 1.5rem;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 0.5rem;
  :only-child {
    margin-bottom: 0;
  }
`

export const CaseId = styled.div`
  padding: 0 1.5rem;
  font-size: 12px;
  margin-bottom: 0.25rem;
`

export const SidebarSeparator = styled.div`
    border-bottom: 1px solid rgba(0,0,0,0.1);
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    cursor: default;
    height: 0;
    ${props => props.withTitle && css`
      border: none;
      display: flex;
      align-items: center;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      padding: 0 2rem 0 1.5rem;
      height: auto;
      span {
        text-transform: uppercase;
        color: rgba(0,0,0,0.3);
        font-weight: 600;
        font-size: 11px;
        margin-right: .5rem;
      }
      :after {
        flex-grow: 1;
        content: '';
        border-bottom: 1px solid rgba(0,0,0,0.1);
      }
    `}
`
